import React, { useCallback, useEffect, useState } from 'react';

import { useAccountEffect, useConnect, useConnections } from 'wagmi';
import { injected } from 'wagmi/connectors';

import { ReactComponent as MetaMaskIcon } from '@pkg/assets/icons/v2/metamask.svg';
import { Button } from '@pkg/components';
import { useMint } from '@pkg/hooks';

import { NftMintSteps } from '@app/client';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import { VisualizerSection } from '../VisualizerSection';
import { StepContent, Text, Title } from '../YatMintFlow.style';

import { NameYat } from './NameYat';

import {
    MetamaskStatusDot,
    MetamaskStatusIndicator,
    MetamaskStatusText,
    StepContentWrapper,
    StepWrapper,
} from './MintingSteps.style';

export function Mint({ currentStep }: { currentStep: NftMintSteps }) {
    const { visualizerFiles, currentEmoji, currentEmojiChainFormat } = useEmojisStore();
    const { connect } = useConnect();
    const { mintYat } = useMint();

    const connections = useConnections();
    const [connectedAccount, setConnectedAccount] = useState<string | undefined>();
    const [isNamingStep, setIsNamingStep] = useState(false);
    const [isRenamingStep, setIsRenamingStep] = useState(false);

    const videoSrc = visualizerFiles?.video;
    useEffect(() => {
        const metamaskConnection = connections?.find((connection) => connection.connector.id === 'metaMask');
        setConnectedAccount(metamaskConnection?.accounts?.[0]);
    }, [connections]);
    useEffect(() => {
        setIsNamingStep(currentStep === NftMintSteps.NameNft);
        setIsRenamingStep(currentStep === NftMintSteps.RenameNft);
    }, [currentStep]);

    useAccountEffect({
        onConnect(data) {
            if (data.connector.id === 'metaMask') {
                setConnectedAccount(data.address);
            }
        },
    });
    const metamaskCTAMarkup = connectedAccount ? (
        <>
            <Button startIcon={<MetaMaskIcon />} onClick={() => setIsNamingStep(true)}>
                Continue with Metamask
            </Button>
        </>
    ) : (
        <>
            <Button
                onClick={() => connect({ connector: injected({ target: 'metaMask' }) })}
                startIcon={<MetaMaskIcon />}
            >
                Sign into Metamask
            </Button>
        </>
    );

    const handleMint = useCallback(async () => {
        if (!connectedAccount) return;

        await mintYat({
            token: currentEmojiChainFormat || currentEmoji,
            account: connectedAccount,
        });
    }, [connectedAccount, currentEmoji, currentEmojiChainFormat, mintYat]);

    const metaMaskStatusMarkup = connectedAccount ? (
        <MetamaskStatusIndicator>
            <MetaMaskIcon />
            <MetamaskStatusDot />
            <MetamaskStatusText>MetaMask wallet connected</MetamaskStatusText>
        </MetamaskStatusIndicator>
    ) : null;

    const showVisualizer = videoSrc && !isNamingStep;
    const showMintIntro = !isNamingStep && !isRenamingStep;
    const visualizerMarkup = showVisualizer ? <VisualizerSection key="video" src={videoSrc} /> : null;
    const mintIntroMarkup = showMintIntro ? (
        <StepContentWrapper key="mint-intro">
            <Title>Mint your Yat</Title>
            <Text>
                Once your Yat is minted, it will exist on the Ethereum blockchain. <br /> This means you can keep it in
                your wallet, and showcase it on NFT marketplaces. Yay! 🎉
            </Text>
        </StepContentWrapper>
    ) : null;

    const namingMarkup =
        isNamingStep || isRenamingStep ? <NameYat currentStep={currentStep} onUpdate={handleMint} /> : null;

    return (
        <StepContent>
            <StepWrapper>
                {mintIntroMarkup}
                {visualizerMarkup}
                {namingMarkup}
                {showMintIntro && (
                    <StepContentWrapper key="mint-ctas">
                        {metamaskCTAMarkup}
                        {metaMaskStatusMarkup}
                    </StepContentWrapper>
                )}
            </StepWrapper>
        </StepContent>
    );
}
